// src/pages/Admin/Message/MessageAdmin.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAllMessages,
  deleteMessageById,
} from "./../../../services/messageServices";
import { getAdminsAndInstructorsIds } from "./../../../services/userServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import Button from "./../../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import { FaEnvelopeOpenText, FaSearch, FaFilter } from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const SUBJECTS = [
  "Adhésion",
  "Dossier d'inscription",
  "Règlement",
  "Informations cours",
  "Absence",
  "Inscription compétition",
  "Perte d'objet",
  "Autre",
];

const subjectBadgeClasses = (subject) => {
  // tons doux + dark mode lisible
  switch (subject) {
    case "Adhésion":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
    case "Dossier d'inscription":
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200";
    case "Règlement":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
    case "Informations cours":
      return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200";
    case "Absence":
      return "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200";
    case "Inscription compétition":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
    case "Perte d'objet":
      return "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200";
    case "Autre":
    default:
      return "bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200";
  }
};

const formatDateTime = (dateTimeString) => {
  try {
    const date = new Date(dateTimeString);
    const opts = {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    return date.toLocaleDateString("fr-FR", opts).replace(",", " -");
  } catch {
    return "—";
  }
};

const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export default function MessageAdmin() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [messages, setMessages] = useState([]);
  const [adminInstructorIds, setAdminInstructorIds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingIds, setLoadingIds] = useState(true);

  const [searchSender, setSearchSender] = useState("");
  const [searchRecipient, setSearchRecipient] = useState("");
  const [searchSubject, setSearchSubject] = useState("");
  const [showAllMessages, setShowAllMessages] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllMessages();
      // on masque les copies
      const base = Array.isArray(data) ? data.filter((m) => !m?.is_copy) : [];
      setMessages(base);
    } catch {
      toast.error("Erreur lors du chargement des messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIds = useCallback(async () => {
    setLoadingIds(true);
    try {
      const idsRes = await getAdminsAndInstructorsIds();
      setAdminInstructorIds(
        Array.isArray(idsRes) ? idsRes.map((x) => x.id) : []
      );
    } catch {
      toast.error("Erreur lors du chargement des rôles.");
    } finally {
      setLoadingIds(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchIds();
  }, [fetchAll, fetchIds]);

  const filteredMessages = useMemo(() => {
    let arr = messages;

    if (!showAllMessages && adminInstructorIds.length) {
      arr = arr.filter((m) => !adminInstructorIds.includes(m?.sender_id));
    }

    if (searchSender.trim()) {
      const q = searchSender.toLowerCase();
      arr = arr.filter((m) =>
        `${m?.Sender?.nom || ""} ${m?.Sender?.prenom || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (searchRecipient.trim()) {
      const q = searchRecipient.toLowerCase();
      arr = arr.filter((m) =>
        `${m?.Recipient?.nom || ""} ${m?.Recipient?.prenom || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (searchSubject.trim()) {
      const q = searchSubject.toLowerCase();
      arr = arr.filter((m) => (m?.subject || "").toLowerCase() === q);
    }

    return arr;
  }, [
    messages,
    adminInstructorIds,
    showAllMessages,
    searchSender,
    searchRecipient,
    searchSubject,
  ]);

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce message ?"))
      return;
    try {
      await deleteMessageById(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.success("Message supprimé avec succès.");
    } catch {
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  const resetFilters = () => {
    setSearchSender("");
    setSearchRecipient("");
    setSearchSubject("");
    setShowAllMessages(false);
  };

  return (
    <section className="w-full">
      {/* Offset fiable pour la navbar */}
      <div className="pt-[150px]" aria-hidden />
      <ScrollIndicator />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
                <HeaderTitle
                  role="Administrateur"
                  title="Gestion des Messages"
                  align="center"
                  className="mb-1"
                />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau gradient */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaEnvelopeOpenText />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Tous les messages reçus et envoyés
                  </p>
                  <p className="text-white/90 text-sm">
                    Filtre par expéditeur, destinataire et sujet. Supprime les
                    messages inappropriés.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  text="Réinitialiser"
                  icon={FaFilter}
                  onClick={resetFilters}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 sm:px-8 py-5 border-b border-slate-200 dark:border-slate-800">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
              {/* Expéditeur */}
              <div className="lg:col-span-2">
                <label className="sr-only" htmlFor="search-sender">
                  Rechercher par expéditeur
                </label>
                <div className="relative">
                  <input
                    id="search-sender"
                    type="text"
                    placeholder="Rechercher par expéditeur…"
                    value={searchSender}
                    onChange={(e) => setSearchSender(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              {/* Destinataire */}
              <div className="lg:col-span-2">
                <label className="sr-only" htmlFor="search-recipient">
                  Rechercher par destinataire
                </label>
                <div className="relative">
                  <input
                    id="search-recipient"
                    type="text"
                    placeholder="Rechercher par destinataire…"
                    value={searchRecipient}
                    onChange={(e) => setSearchRecipient(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              {/* Sujet */}
              <div className="lg:col-span-1">
                <select
                  value={searchSubject}
                  onChange={(e) => setSearchSubject(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">— Sujet —</option>
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Switch & compteur */}
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <label className="inline-flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showAllMessages}
                  onChange={(e) => setShowAllMessages(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-violet-600 focus:ring-violet-400"
                />
                Afficher aussi les messages envoyés par l’équipe
                (admin/enseignants)
              </label>

              <div className="text-sm text-slate-600 dark:text-slate-300">
                {loading || loadingIds
                  ? "Chargement…"
                  : `${filteredMessages.length} message${
                      filteredMessages.length > 1 ? "s" : ""
                    }`}
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading */}
            {(loading || loadingIds) && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && !loadingIds && filteredMessages.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun message à afficher
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajuste tes filtres ou réinitialise-les.
                </p>
              </div>
            )}

            {/* Table desktop */}
            {!loading && !loadingIds && filteredMessages.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {[
                            "Expéditeur",
                            "Destinataire",
                            "Sujet",
                            "Message",
                            "Date",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filteredMessages.map((m) => {
                          const sender =
                            `${m?.Sender?.nom || ""} ${
                              m?.Sender?.prenom || ""
                            }`.trim() || "—";
                          const recipient =
                            `${m?.Recipient?.nom || ""} ${
                              m?.Recipient?.prenom || ""
                            }`.trim() || "—";
                          const subject = m?.subject || "Autre";
                          const contentPreview = stripHtml(m?.content).slice(
                            0,
                            220
                          );
                          const date = formatDateTime(m?.created_at);
                          return (
                            <tr
                              key={m.id}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                                {sender}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {recipient}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${subjectBadgeClasses(
                                    subject
                                  )}`}
                                >
                                  {subject}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                <span
                                  title={stripHtml(m?.content)}
                                  className="block max-w-[520px] truncate"
                                >
                                  {contentPreview}
                                  {contentPreview.length >= 220 ? "…" : ""}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                                {date}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end">
                                  <ButtonDelete
                                    onClick={() => handleDeleteMessage(m.id)}
                                    hoverLabel="Supprimer"
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes mobile */}
            {!loading && !loadingIds && filteredMessages.length > 0 && (
              <div className="md:hidden space-y-3">
                {filteredMessages.map((m) => {
                  const sender =
                    `${m?.Sender?.nom || ""} ${
                      m?.Sender?.prenom || ""
                    }`.trim() || "—";
                  const recipient =
                    `${m?.Recipient?.nom || ""} ${
                      m?.Recipient?.prenom || ""
                    }`.trim() || "—";
                  const subject = m?.subject || "Autre";
                  const date = formatDateTime(m?.created_at);
                  return (
                    <div
                      key={`card-${m.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${subjectBadgeClasses(
                              subject
                            )}`}
                          >
                            {subject}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          {date}
                        </div>
                      </div>

                      <div className="mt-2 text-sm">
                        <div className="text-slate-800 dark:text-slate-100 font-medium">
                          {sender}{" "}
                          <span className="text-slate-500 dark:text-slate-400">
                            →
                          </span>{" "}
                          {recipient}
                        </div>
                        <div className="mt-1 text-slate-700 dark:text-slate-300">
                          {/* On affiche le contenu brut (sans HTML) en mobile */}
                          {stripHtml(m?.content)}
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-end">
                        <ButtonDelete
                          onClick={() => handleDeleteMessage(m.id)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>
    </section>
  );
}
